import { useState } from "react";
import { IconAlertTriangle, IconClock } from "@tabler/icons-react";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { analytics } from "../../analytics/analyticsEvents";
import { getContractById, skills as allSkills, tools as allTools } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import { calculateLogAnalysisScore } from "../../engine/logAnalysisScore";
import { getGrade } from "../../engine/scoring";
import { suitableSkillsForType, suitableToolsForType } from "../../engine/loadout";
import { highlightedLogIndices } from "../../engine/toolHints";
import { isOddHourTimestamp } from "../../engine/skillEffects";
import { severityText } from "../../i18n/enumText";
import { toneTooltip } from "../../i18n/tooltips";
import { useGameStore } from "../../state/store";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { GradeSeal } from "../common/GradeSeal";
import { Panel } from "../common/Panel";
import { PICK_VERDICT_VIEW, countPickVerdicts, pickVerdictFor } from "../common/pickVerdict";
import { RiskBadge } from "../common/RiskBadge";
import { StatTile } from "../common/StatTile";
import { cx } from "../common/cx";
import { useGameplaySession } from "../common/useGameplaySession";
import { LoadoutPicker } from "../loadout/LoadoutPicker";
import { useLoadoutNames } from "../loadout/useLoadoutNames";
import styles from "./LogAnalysis.module.css";

export function LogAnalysis(): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const { skillName } = useLoadoutNames();
  const activeContractId = useGameStore((state) => state.activeContractId);
  const activeToolIds = useGameStore((state) => state.activeToolIds);
  const activeSkillIds = useGameStore((state) => state.activeSkillIds);
  const player = useGameStore((state) => state.player);
  const toggleActiveTool = useGameStore((state) => state.toggleActiveTool);
  const toggleActiveSkill = useGameStore((state) => state.toggleActiveSkill);
  const logAnalysisLevel = useGameStore((state) =>
    state.activeSkillIds.includes("log_analysis") ? (state.player.skills.log_analysis ?? 0) : 0,
  );
  const navigateTo = useGameStore((state) => state.navigateTo);
  const completeTask = useGameStore((state) => state.completeTask);

  const contract = activeContractId ? getContractById(activeContractId) : undefined;
  const events = contract?.logEvents ?? [];

  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  useGameplaySession("log_analysis");

  const showSeverity = submitted || activeToolIds.includes("severity_highlighter");
  const filterIndices =
    !submitted && activeToolIds.includes("log_filter")
      ? new Set(highlightedLogIndices(events))
      : null;
  const anomalyHintIndices =
    !submitted && logAnalysisLevel >= 1
      ? new Set(events.map((e, i) => (isOddHourTimestamp(e.timestamp) ? i : -1)).filter((i) => i >= 0))
      : null;

  if (!contract || events.length === 0) {
    return (
      <EmptyState
        title={<Trans>Нет данных для анализа</Trans>}
        action={
          <Button variant="secondary" onClick={() => navigateTo("workspace")}>
            <Trans>Назад</Trans>
          </Button>
        }
      />
    );
  }

  const toggle = (index: number): void => {
    if (submitted) {
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const submit = (): void => {
    const fpGuard = activeToolIds.includes("false_positive_guard");
    const result = calculateLogAnalysisScore(events, selected, fpGuard, logAnalysisLevel);
    setScore(result);
    setSubmitted(true);
    analytics.taskSubmitted({ type: "log_analysis", score: result });
  };

  const finish = (): void => {
    completeTask(score);
    navigateTo("workspace");
  };

  const pickerTools = suitableToolsForType("log_analysis", player.ownedTools, allTools);
  const pickerSkills = suitableSkillsForType("log_analysis", player.skills, allSkills);

  const grade = getGrade(score);
  const verdictCounts = countPickVerdicts(
    events.map((event) => ({ positive: event.isSuspicious })),
    selected,
  );

  return (
    <div className={styles.screen}>
      <LoadoutPicker
          tools={pickerTools}
          skills={pickerSkills}
          skillLevels={player.skills}
          activeToolIds={activeToolIds}
          activeSkillIds={activeSkillIds}
          readOnly={submitted}
          onToggleTool={toggleActiveTool}
          onToggleSkill={toggleActiveSkill}
        />
      <Panel
        title={<Trans>Анализ логов</Trans>}
        actions={
          submitted ? (
            <span className={cx(styles.scoreTag, "mono")}>
              <Trans>Очки: {score}</Trans>
            </span>
          ) : undefined
        }
      >
        {submitted ? (
          <div className={styles.result}>
            <GradeSeal grade={grade} score={score} />
            <div className={styles.resultStats}>
              <StatTile label={<Trans>Верно</Trans>} value={`${verdictCounts.correct}`} tone="accent" />
              <StatTile label={<Trans>Ложные</Trans>} value={`${verdictCounts.falsePositive}`} />
              <StatTile label={<Trans>Пропущено</Trans>} value={`${verdictCounts.missed}`} />
              <StatTile label={<Trans>Счёт</Trans>} value={`${score}`} />
            </div>
          </div>
        ) : (
          <p className={styles.hint}>
            <Trans>Отметь подозрительные события в журнале входов.</Trans>
          </p>
        )}

        <ul className={styles.rows}>
          {events.map((event, index) => {
            const picked = selected.has(index);
            const verdict = pickVerdictFor(event.isSuspicious, picked);
            const dimmed = filterIndices !== null && !filterIndices.has(index);
            const anomalyHinted = anomalyHintIndices?.has(index) ?? false;
            return (
              <li
                key={index}
                data-tutorial={event.isSuspicious ? "suspicious-row" : undefined}
                className={cx(
                  styles.row,
                  dimmed && styles.dimmed,
                  anomalyHinted && styles.hinted,
                  picked && !submitted && styles.picked,
                  submitted && styles[verdict],
                )}
              >
                {anomalyHinted && (
                  <div className={styles.toolHint}>
                    <IconClock size={14} aria-hidden="true" />
                    {skillName("log_analysis")}: <Trans>необычное время активности</Trans>
                  </div>
                )}
                <button
                  type="button"
                  className={styles.rowMain}
                  onClick={() => toggle(index)}
                  aria-pressed={picked}
                  disabled={submitted}
                >
                  <span className={cx(styles.time, "mono")}>{event.timestamp}</span>
                  <span className={cx(styles.actor, "mono")}>{event.actor}</span>
                  <span className={styles.action}>{t(event.action)}</span>
                  <span className={cx(styles.target, "mono")}>{event.target}</span>
                  {showSeverity && (
                    <RiskBadge tone={event.severity} label={severityText(event.severity)} tooltip={i18n._(toneTooltip(event.severity))} tooltipTabIndex={-1} />
                  )}
                  <span className={styles.flag}>
                    {picked ? (
                      <span className={styles.flagOn}>
                        <IconAlertTriangle size={15} aria-hidden="true" />
                        <Trans>Подозрительно</Trans>
                      </span>
                    ) : (
                      <span className={styles.flagOff}>
                        <Trans>Норма</Trans>
                      </span>
                    )}
                  </span>
                </button>
                {submitted && (
                  <div className={styles.feedback}>
                    <span className={styles.verdict}>
                      {PICK_VERDICT_VIEW[verdict].icon}
                      {PICK_VERDICT_VIEW[verdict].label}
                    </span>
                    <span className={styles.explanation}>{t(event.explanation)}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <div className={styles.footer}>
          {submitted ? (
            <span data-tutorial="task-continue">
              <Button variant="primary" onClick={finish}>
                <Trans>Продолжить</Trans>
              </Button>
            </span>
          ) : (
            <>
              {selected.size === 0 && (
                <span className={styles.footerHint}>
                  <Trans>Отметь хотя бы одно событие, чтобы проверить.</Trans>
                </span>
              )}
              <Button variant="primary" onClick={submit} disabled={selected.size === 0}>
                <Trans>Проверить</Trans>
              </Button>
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}
